import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Star, Shield, User, X, Key } from 'lucide-react';

const PrestataireUsers = () => {
  const [prestataires, setPrestataires] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [combined, setCombined] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    nom: '', 
    prenom: '', 
    email: '', 
    telephone: '', 
    roles: [],  // Changed from 'role' to 'roles' array
    is_jeremy: false, 
    password: '', 
    is_admin: false
  });

  useEffect(() => {
    loadData();
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const res = await axios.get(`${API}/prestataire-roles`);
      setAvailableRoles(res.data);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [prestRes, userRes] = await Promise.all([
        axios.get(`${API}/prestataires`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      
      setPrestataires(prestRes.data || []);
      setUsers(userRes.data || []);
      
      // Combine prestataires and users
      const combinedData = [];
      
      // Add all prestataires with their account status
      prestRes.data.forEach(prest => {
        const userAccount = userRes.data.find(u => u.email === prest.email);
        combinedData.push({
          ...prest,
          type: 'prestataire',
          hasAccount: !!userAccount,
          isAdmin: userAccount?.role === 'admin',
          userId: userAccount?.id,
          roles: prest.roles || (prest.role ? [prest.role] : [])  // Migration: convert old 'role' to 'roles'
        });
      });
      
      // Add users who don't have a prestataire entry
      userRes.data.forEach(user => {
        if (!prestRes.data.find(p => p.email === user.email)) {
          combinedData.push({
            id: user.id,
            nom: user.name?.split(' ').pop() || '',
            prenom: user.name?.split(' ')[0] || '',
            email: user.email,
            telephone: '',
            roles: ['admin'],
            type: 'user',
            hasAccount: true,
            isAdmin: user.role === 'admin',
            userId: user.id
          });
        }
      });
      
      setCombined(combinedData);
    } catch (error) {
      console.error('Erreur chargement prestataires:', error);
      toast.error('Erreur lors du chargement: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let prestataireId = editingItem?.id;
      
      // Create or update prestataire
      if (editingItem && editingItem.type === 'prestataire') {
        // Update prestataire
        await axios.put(`${API}/prestataires/${editingItem.id}`, {
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          roles: formData.roles,
          is_jeremy: formData.is_jeremy
        }, { withCredentials: true });
        
        // If email changed, update user account too
        if (editingItem.hasAccount && editingItem.email !== formData.email) {
          await axios.put(`${API}/users/${editingItem.userId}`, {
            email: formData.email
          }, { withCredentials: true });
        }
        
        toast.success('Prestataire mis à jour');
      } else if (!editingItem) {
        const response = await axios.post(`${API}/prestataires`, {
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          roles: formData.roles,
          is_jeremy: formData.is_jeremy
        }, { withCredentials: true });
        prestataireId = response.data.id;
        toast.success('Prestataire créé');
      }
      
      // Create user account if password is provided and email is present
      if (formData.password && formData.password.length >= 8 && prestataireId && formData.email) {
        try {
          await axios.post(
            `${API}/prestataires/${prestataireId}/create-account`,
            { 
              password: formData.password, 
              role: formData.is_admin ? 'admin' : 'prestataire' 
            },
            { withCredentials: true }
          );
          toast.success(`Compte ${formData.is_admin ? 'administrateur' : 'prestataire'} créé avec succès !`);
        } catch (err) {
          toast.warning('Erreur lors de la création du compte: ' + (err.response?.data?.detail || err.message));
        }
      }
      
      loadData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nom: item.nom,
      prenom: item.prenom,
      email: item.email || '',
      telephone: item.telephone || '',
      roles: item.roles || [],
      is_jeremy: item.is_jeremy || false,
      password: '',
      is_admin: item.isAdmin || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (item) => {
    // Protection: only jeremy.ferrar@gmail.com can delete admins
    if (item.isAdmin && currentUser?.email !== 'jeremy.ferrar@gmail.com') {
      toast.error('Seul Jeremy peut supprimer des administrateurs');
      return;
    }
    
    if (!window.confirm(`Supprimer ${item.prenom} ${item.nom} ?`)) return;
    
    try {
      // Delete user account if exists
      if (item.userId) {
        await axios.delete(`${API}/users/${item.userId}`, { withCredentials: true });
      }
      
      // Delete prestataire entry if exists
      if (item.type === 'prestataire') {
        await axios.delete(`${API}/prestataires/${item.id}`, { withCredentials: true });
      }
      
      toast.success('Supprimé avec succès');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleAdmin = async (item) => {
    // Protection: only jeremy.ferrar@gmail.com can modify admin status
    if (currentUser?.email !== 'jeremy.ferrar@gmail.com') {
      toast.error('Seul Jeremy peut modifier le statut administrateur');
      return;
    }
    
    if (!item.hasAccount) {
      toast.error('Créez d\'abord un compte de connexion pour cet utilisateur');
      return;
    }
    
    try {
      const newRole = item.isAdmin ? 'prestataire' : 'admin';
      await axios.put(
        `${API}/users/${item.userId}`,
        { role: newRole },
        { withCredentials: true }
      );
      toast.success(`Rôle mis à jour : ${newRole === 'admin' ? 'Administrateur' : 'Prestataire'}`);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification du rôle');
    }
  };

  const toggleRole = (roleValue) => {
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      if (currentRoles.includes(roleValue)) {
        return { ...prev, roles: currentRoles.filter(r => r !== roleValue) };
      } else {
        return { ...prev, roles: [...currentRoles, roleValue] };
      }
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    try {
      await axios.post(
        `${API}/users/${resetPasswordUser.userId}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Mot de passe réinitialisé avec succès');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      setResetPasswordUser(null);
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation du mot de passe');
    }
  };

  const openPasswordDialog = (item) => {
    if (!item.hasAccount) {
      toast.error('Cet utilisateur n\'a pas de compte de connexion');
      return;
    }
    setResetPasswordUser(item);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ 
      nom: '', 
      prenom: '', 
      email: '', 
      telephone: '', 
      roles: [], 
      is_jeremy: false, 
      password: '', 
      is_admin: false 
    });
    setEditingItem(null);
  };

  const getRoleBadge = (roleValue) => {
    const role = availableRoles.find(r => r.value === roleValue);
    const colors = { 
      videaste: 'bg-blue-100 text-blue-700', 
      photographe: 'bg-purple-100 text-purple-700', 
      magnets: 'bg-amber-100 text-amber-700', 
      drone: 'bg-emerald-100 text-emerald-700',
      admin: 'bg-rose-100 text-rose-700'
    };
    const label = role ? role.label : roleValue;
    return <Badge className={colors[roleValue] || 'bg-stone-100 text-stone-700'}>{label}</Badge>;
  };

  const isJeremy = currentUser?.email === 'jeremy.ferrar@gmail.com';

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Prestataires & Utilisateurs</h1>
          <p className="text-stone-600">Gérez votre équipe et les accès</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="btn-add-user">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau prestataire
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Modifier' : 'Nouveau prestataire'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom *</Label>
                  <Input 
                    value={formData.nom} 
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })} 
                    required 
                  />
                </div>
                <div>
                  <Label>Prénom *</Label>
                  <Input 
                    value={formData.prenom} 
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} 
                    required 
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email (optionnel)</Label>
                  <Input 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    placeholder="Pour créer un compte de connexion"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Nécessaire uniquement pour créer un compte de connexion
                  </p>
                </div>
                <div>
                  <Label>Téléphone (optionnel)</Label>
                  <Input 
                    value={formData.telephone} 
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} 
                  />
                </div>
              </div>
              
              {/* Multi-select for roles */}
              <div>
                <Label>Rôles métier (sélection multiple)</Label>
                <div className="mt-2 border rounded-lg p-3 bg-stone-50 space-y-2">
                  {availableRoles.map(role => (
                    <div key={role.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${role.id}`}
                        checked={formData.roles.includes(role.value)}
                        onCheckedChange={() => toggleRole(role.value)}
                      />
                      <Label htmlFor={`role-${role.id}`} className="cursor-pointer text-sm">
                        {role.label}
                      </Label>
                    </div>
                  ))}
                </div>
                {formData.roles.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">⚠️ Aucun rôle sélectionné</p>
                )}
              </div>

              {!editingItem && (
                <>
                  <div>
                    <Label>Mot de passe (optionnel)</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Minimum 8 caractères pour créer un compte"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Si fourni (8+ caractères), un compte de connexion sera créé automatiquement
                    </p>
                    {formData.password && formData.password.length > 0 && formData.password.length < 8 && (
                      <p className="text-xs text-rose-600 mt-1">⚠️ Le mot de passe doit contenir au moins 8 caractères</p>
                    )}
                  </div>
                  {formData.password && formData.password.length >= 8 && (
                    <div className="flex items-center space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <Checkbox
                        id="is_admin"
                        checked={formData.is_admin}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                      />
                      <div>
                        <Label htmlFor="is_admin" className="cursor-pointer font-semibold">
                          👑 Administrateur
                        </Label>
                        <p className="text-xs text-stone-600">
                          {formData.is_admin 
                            ? '✓ Accès complet au backoffice' 
                            : '✓ Accès uniquement aux feuilles de route assignées'
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_jeremy"
                  checked={formData.is_jeremy}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_jeremy: checked })}
                />
                <Label htmlFor="is_jeremy">C&apos;est moi (Jeremy) - Coût = 0€</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                  {editingItem ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Rôles métier</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">Chargement...</TableCell>
                </TableRow>
              ) : combined.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-stone-500">
                    Aucun prestataire
                  </TableCell>
                </TableRow>
              ) : (
                combined.map((item) => (
                  <TableRow key={`${item.type}-${item.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{item.prenom} {item.nom}</div>
                        {item.is_jeremy && <Star className="h-4 w-4 text-amber-500 fill-amber-500" />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{item.email || <span className="text-stone-400 italic">Pas d&apos;email</span>}</div>
                      {item.telephone && <div className="text-sm text-stone-500">{item.telephone}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(item.roles && item.roles.length > 0) ? (
                          item.roles.map((roleValue, idx) => (
                            <span key={idx}>{getRoleBadge(roleValue)}</span>
                          ))
                        ) : (
                          <span className="text-xs text-stone-400 italic">Aucun rôle</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {item.hasAccount ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-700">
                              <User className="h-3 w-3 mr-1" />
                              Compte actif
                            </Badge>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-stone-500">
                            <User className="h-3 w-3 mr-1" />
                            Pas de compte
                          </Badge>
                        )}
                        {item.isAdmin && (
                          <Badge className="bg-rose-100 text-rose-700">
                            <Shield className="h-3 w-3 mr-1" />
                            Admin
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Toggle Admin - Only Jeremy can do this */}
                        {item.hasAccount && isJeremy && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleAdmin(item)}
                            title={item.isAdmin ? 'Retirer droits admin' : 'Rendre administrateur'}
                          >
                            <Shield className={`h-4 w-4 ${item.isAdmin ? 'text-rose-600' : 'text-stone-400'}`} />
                          </Button>
                        )}
                        
                        {/* Reset Password - Only for users with accounts */}
                        {item.hasAccount && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPasswordDialog(item)}
                            title="Réinitialiser le mot de passe"
                          >
                            <Key className="h-4 w-4 text-amber-600" />
                          </Button>
                        )}
                        
                        {/* Edit */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEdit(item)}
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        {/* Delete - Only Jeremy can delete admins */}
                        {(!item.isAdmin || isJeremy) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(item)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!isJeremy && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            ℹ️ Seul Jeremy peut modifier le statut administrateur et supprimer des administrateurs.
          </p>
        </div>
      )}

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
          </DialogHeader>
          {resetPasswordUser && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  Vous allez réinitialiser le mot de passe de :<br />
                  <strong>{resetPasswordUser.prenom} {resetPasswordUser.nom}</strong> ({resetPasswordUser.email})
                </p>
              </div>
              
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="new_password">Nouveau mot de passe *</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 8 caractères"
                    required
                    data-testid="input-reset-new-password"
                  />
                  {newPassword && newPassword.length < 8 && (
                    <p className="text-xs text-rose-600 mt-1">⚠️ Le mot de passe doit contenir au moins 8 caractères</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="confirm_password">Confirmer le mot de passe *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmez le mot de passe"
                    required
                    data-testid="input-reset-confirm-password"
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-rose-600 mt-1">⚠️ Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPasswordDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="bg-amber-600 hover:bg-amber-700"
                    disabled={!newPassword || !confirmPassword || newPassword.length < 8}
                    data-testid="btn-reset-password-submit"
                  >
                    Réinitialiser le mot de passe
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrestataireUsers;
